<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { CURRENCY } from '@Constants';
import { applyShelterCurrency } from '@Helpers';

const emit = defineEmits<{
  onFieldUpdate: [newValue: any, originalData: any, field: string];
  onCancelEdit: [];
}>();

const props = defineProps<{
  field?: any;
  fieldName?: string;
  isEditMode?: boolean;
  rowData?: any;
  settings?: any;
}>();

const editContainerRef = useTemplateRef<HTMLElement>('editContainerRef');

const numValue = typeof props.field === 'number' ? props.field : parseFloat(props.field) || 0;
const model = ref<number>(numValue);
const initialValue = ref<number>(numValue);

const originalValue = computed(() => {
  const original = props.rowData?.original_retail_cost ?? props.rowData?.medical_costs_settings?.original_retail_cost;
  // prettier-ignore
  return original !== undefined && original !== null ? typeof original === 'number'
    ? original : parseFloat(original) || 0
    : null;
});

const hasOriginalValue = computed(() => {
  if (originalValue.value === null) return false;
  const currentValueHasDifference = Math.abs(model.value - originalValue.value) >= 0.001;

  return currentValueHasDifference;
});

const enableClickOutside = computed(
  () => !!props.settings?.enableClickOutside || !!props.field?.settings?.enableClickOutside
);

watch(() => props.isEditMode, onEditModeChange);
watch(() => props.field, onFieldUpdate);

onClickOutside(editContainerRef, async (event) => await onEditClickOutside(event));

async function onEditClickOutside(event: Event) {
  if (!enableClickOutside.value) return;

  if (!props.isEditMode) return;

  // NOTE :: Wait for click events to process first (allows button clicks to fire)
  await nextTick();

  // NOTE :: Check if we're still in edit mode (if not, a button was clicked and handled it)
  if (!props.isEditMode) return;

  // NOTE :: Don't cancel if clicking on a button or inside the edit container
  const target = event.target as HTMLElement;
  const isButton = target.closest('button');
  const isInsideContainer = editContainerRef.value?.contains(target);

  if (isButton || isInsideContainer) return;

  onCancel();
}

function onFieldUpdate(newValue: any) {
  if (props.isEditMode) return;

  const numValue = typeof newValue === 'number' ? newValue : parseFloat(newValue) || 0;
  if (model.value !== numValue) {
    model.value = numValue;
    initialValue.value = numValue;
  }
}

function onEditModeChange() {
  const currentValue = typeof props.field === 'number' ? props.field : parseFloat(props.field) || 0;

  model.value = currentValue;
  initialValue.value = currentValue;
}

function onSave() {
  if (!props.rowData || !props.fieldName) return;

  const isValueChanged = model.value !== initialValue.value;
  if (!isValueChanged) {
    emit('onCancelEdit');
    return;
  }

  emit('onFieldUpdate', model.value, props.rowData, props.fieldName);
  initialValue.value = model.value;
}

function onCancel() {
  model.value = initialValue.value;

  emit('onCancelEdit');
}
</script>

<template>
  <div
    v-if="!isEditMode"
    class="flex items-center justify-start gap-2"
  >
    <span
      v-if="hasOriginalValue && originalValue"
      class="mt- absolute left-2.5 top-0.5 text-xs text-gray-500 line-through"
    >
      {{ applyShelterCurrency(originalValue) }}
    </span>

    <span :class="hasOriginalValue ? 'mt-1.5' : ''">{{ applyShelterCurrency(model) }}</span>
  </div>

  <div
    v-else
    class="flex items-center gap-2"
    ref="editContainerRef"
  >
    <InputNumber
      v-model="model"
      mode="currency"
      :class="['h-9', settings?.class || 'w-[100px]']"
      :currency="settings?.currency || CURRENCY.USD"
      :maxFractionDigits="2"
      :minFractionDigits="2"
      :pt:input:root:class="['text-sm', 'w-[100px]']"
      :step="0.01"
      @keydown.enter="onSave"
    />

    <div class="flex gap-2">
      <Button
        v-tooltip.top="$t('common.save')"
        outlined
        class="h-7 w-7 rounded-md bg-white p-1 !text-xs"
        icon="pi pi-check text-xs"
        severity="success"
        @click.stop="onSave"
      />

      <Button
        v-tooltip.top="$t('common.cancel')"
        outlined
        class="h-7 w-7 rounded-md bg-white p-1 !text-xs"
        icon="pi pi-times text-xs"
        pt:icon="text-red-500"
        severity="danger"
        @click.stop="onCancel"
      />
    </div>
  </div>
</template>
